import { SensorOrSubSensorInfoByType } from '@components/cockpit/panels/monitoring/monitoring.types';
import { getMetricValue, getOperatingStatusValue } from '@components/cockpit/panels/monitoring/monitoring.utils';
import { OtherUnits, PressureUnits, TemperatureUnits, TimeUnits, VoltageUnits } from '@utils/unit.utils';

import { SensorTypeEnum, VideoStateEnum } from '@/types/sensor/sensors';

import { SensorSection, SensorSectionFactory } from '../../../GlobalTab';
import Section from '../../Section';
import { maintenanceAndCommandSection } from '../MaintenanceAndCommandSection';
import HolopticsSchematic from './HolopticsSchematic';

type HolopticsSectionProps = SensorOrSubSensorInfoByType<SensorTypeEnum.AF2206 | SensorTypeEnum.AXIS>;

function HolopticsWebRelayBaie({ status }: Readonly<HolopticsSectionProps>): SensorSection {
  const baieStatus = status?.systemStatus;
  return [
    <Section
      key="holoptics-baie"
      operatingValues={
        new Map([
          ['webrelay', { value: baieStatus?.wrConnected }],
          ['airConditioning', { value: baieStatus?.acOn }],
          ['ventilation', { value: baieStatus?.ventilationOn }],
        ])
      }
      metricsValues={
        new Map([
          ['temperature', getMetricValue(TemperatureUnits.CELCIUS, baieStatus?.baieTemperature)],
          ['humidity', getMetricValue(OtherUnits.PERCENT, baieStatus?.baieHumidity)],
          ['webRelayVoltage', getMetricValue(VoltageUnits.VOLT, baieStatus?.baieVin)],
        ])
      }
      informationValues={new Map([['webrelay', 'x400']])}
    />,
    null,
  ];
}

function HolopticsWebRelayCabinet(): SensorSection {
  return [
    <Section
      key="holoptics-cabinet"
      operatingValues={new Map([['webrelay', { value: null }]])}
      metricsValues={
        new Map([
          ['temperature', { value: null, state: null }],
          ['humidity', { value: null, state: null }],
          ['voltage', { value: null, state: null }],
        ])
      }
      informationValues={new Map([['webrelay', 'x400']])}
    />,
    null,
  ];
}

function HolopticsTurret({ status }: Readonly<HolopticsSectionProps>): SensorSection {
  const turretStatus = status?.turretStatus;

  return [
    <Section
      key="holoptics-turret"
      operatingValues={new Map([['motorPower', { value: turretStatus?.motorPower }]])}
      metricsValues={
        new Map([
          ['temperature', getMetricValue(TemperatureUnits.CELCIUS, turretStatus?.temperature)],
          ['humidity', getMetricValue(OtherUnits.PERCENT, turretStatus?.humidity)],
          ['pressure', getMetricValue(PressureUnits.HECTOPASCAL, turretStatus?.pressure)],
        ])
      }
      informationValues={
        new Map([
          ['bootloaderVersion', turretStatus?.bootLoader],
          ['softwareVersion', turretStatus?.softwareVersion],
          ['serialNumber', turretStatus?.serialNumber],
        ])
      }
    />,
    null,
  ];
}

function HolopticsFlux({ status, fluxId }: Readonly<HolopticsSectionProps & { fluxId: number }>): SensorSection {
  const videoStatus = status?.videoStatuses.find((videoStatus) => videoStatus.id === fluxId);
  const streamerOn = videoStatus?.streamerOn;

  return [
    <Section
      key={`"holoptics-flux-${fluxId}"`}
      operatingValues={
        new Map([
          [
            'optic',
            {
              value: videoStatus?.state === VideoStateEnum.ON,
              time: videoStatus?.runningTime ? videoStatus.runningTime * 1000 : null,
            },
          ],
          ['streamer', { value: streamerOn }],
          ['deIcing', { value: videoStatus?.deIcing }],
        ])
      }
      metricsValues={
        new Map([
          ['temperature', getMetricValue(TemperatureUnits.CELCIUS, videoStatus?.temperature)],
          ['humidity', getMetricValue(OtherUnits.PERCENT, videoStatus?.humidity)],
          ['pressure', getMetricValue(PressureUnits.HECTOPASCAL, videoStatus?.pressure)],
        ])
      }
      informationValues={
        new Map([
          ['bootloaderVersion', videoStatus?.bootLoader],
          ['softwareVersion', videoStatus?.softwareVersion],
          ['serialNumber', videoStatus?.serialNumber],
        ])
      }
    />,
    null,
  ];
}

function HolopticsMWIR({ status }: Readonly<HolopticsSectionProps>): SensorSection {
  const videoStatus = status?.videoStatuses.find((videoStatus) => videoStatus.id === 3);
  const streamerOn = videoStatus?.streamerOn;

  return [
    <Section
      key="holoptics-flux-mwir"
      operatingValues={
        new Map([
          [
            'optic',
            {
              value: videoStatus?.state === VideoStateEnum.ON,
              time: videoStatus?.runningTime ? videoStatus.runningTime * 1000 : null,
            },
          ],
          ['streamer', { value: streamerOn }],
        ])
      }
      metricsValues={
        new Map([
          ['temperature', getMetricValue(TemperatureUnits.CELCIUS, videoStatus?.temperature)],
          ['humidity', getMetricValue(OtherUnits.PERCENT, videoStatus?.humidity)],
          ['pressure', getMetricValue(PressureUnits.HECTOPASCAL, videoStatus?.pressure)],
          ['activityTime', getMetricValue(TimeUnits.HOURS, videoStatus?.workingHours, true)],
        ])
      }
      informationValues={
        new Map([
          ['bootloaderVersion', videoStatus?.bootLoader],
          ['softwareVersion', videoStatus?.softwareVersion],
          ['serialNumber', videoStatus?.serialNumber],
        ])
      }
    />,
    null,
  ];
}

function HolopticsSoftwares({ status }: Readonly<HolopticsSectionProps>): SensorSection {
  return [
    <Section
      key="holoptics-softwares"
      operatingValues={
        new Map([
          ['api', { value: getOperatingStatusValue(status?.apiStatus) }],
          ['trackers', { value: getOperatingStatusValue(status?.trackerStatus) }],
          //['sphinxAgent', { value: null }],
          //['stormshield', { value: null }],
          //['ntpClient', { value: null }],
        ])
      }
      metricsValues={
        new Map([
          //['temperatureCPU', { value: null, state: null }],
          //['temperatureGPU', { value: null, state: null }],
          //['CPUUsage', { value: null, state: null }],
          //['GPUUsage', { value: null, state: null }],
          //['RAMUsage', { value: null, state: null }],
          //['disc', { value: null, state: null }],
          //['inputThroughput', { value: null, state: null }],
          //['outputThroughput', { value: null, state: null }],
        ])
      }
      informationValues={
        new Map([
          ['api', null],
          //['trackers', null],
          //['sphinxAgent', null],
          //['stormshield', null],
          //['ntpClient', null],
          //['cpuFreq', null],
          //['gpuFreq', null],
        ])
      }
    />,
    null,
  ];
}

export const HolopticsSections = new Map<string, SensorSectionFactory<SensorTypeEnum.AF2206 | SensorTypeEnum.AXIS>>([
  ['common.schematic', HolopticsSchematic],
  maintenanceAndCommandSection,
  ['holoptics.webRelayBaie', HolopticsWebRelayBaie],
  ['holoptics.webRelayCabinet', HolopticsWebRelayCabinet],
  ['holoptics.turret', HolopticsTurret],
  ['holoptics.gc', (props: HolopticsSectionProps) => HolopticsFlux({ ...props, fluxId: 1 })],
  ['holoptics.pc', (props: HolopticsSectionProps) => HolopticsFlux({ ...props, fluxId: 0 })],
  ['holoptics.swir', (props: HolopticsSectionProps) => HolopticsFlux({ ...props, fluxId: 2 })],
  ['holoptics.mwir', HolopticsMWIR],
  ['holoptics.softwares', HolopticsSoftwares],
]);
